import { EditOutlined } from '@ant-design/icons';
import {
  cleanPhone,
  describeError,
  dirhams,
  type MonthKey,
  monthLabel,
  PHONE_HINT,
  type SettingsValues,
  toSettingsFields,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Skeleton,
  Switch,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { KeyValues, PageHeader, Section } from './ui';
import {
  SettingsDocument,
  UpdateSettingsDocument,
  useIsCommittee,
} from './water-data';

const days = { min: 1, max: 365, precision: 0 } as const;

/** Labels for the summary before saving (field → how it reads). */
const LABELS: Record<keyof ReturnType<typeof toSettingsFields>, string> = {
  associationName: 'اسم الجمعية',
  address: 'العنوان',
  contactPhone: 'هاتف الاستفسار',
  dueDays: 'أجل الأداء (أيام)',
  firstReminderAfterDays: 'أول تذكير (أيام)',
  reminderEveryDays: 'تذكير كل (أيام)',
  publicArrearsAfterDays: 'لائحة المتأخرين: بعد (أيام)',
  publicArrearsRedAfterDays: 'لائحة المتأخرين: بالأحمر بعد (أيام)',
  publicArrearsMinAmount: 'لائحة المتأخرين: من مبلغ',
  publicArrearsSince: 'لائحة المتأخرين: الفواتير من شهر',
  readingPhotoAlways: 'تصوير العداد مع كل قراءة',
  receiptPhotoMinAmount: 'مبلغ صورة الوصل',
};

function show(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'نعم' : 'لا';
  if (key === 'publicArrearsMinAmount' || key === 'receiptPhotoMinAmount') {
    return dirhams(String(value));
  }
  if (key === 'publicArrearsSince') {
    return monthLabel(String(value).slice(0, 7) as MonthKey);
  }
  return String(value);
}

type FormValues = Omit<SettingsValues, 'publicArrearsSince'> & {
  publicArrearsSince: dayjs.Dayjs | null;
};

/**
 * «الإعدادات»: the association, payment and reminders, photos, the public
 * late-payers list. «حفظ» shows each change (before → after) first.
 */
export function SettingsScreen() {
  const canEdit = useIsCommittee();
  const { message, modal } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [{ data, error }] = useQuery({ query: SettingsDocument });
  const [{ fetching: saving }, update] = useMutation(UpdateSettingsDocument);
  const [problem, setProblem] = useState<string | null>(null);
  // Read first (doc 64); «تعديل» opens the form.
  const [editing, setEditing] = useState(false);
  const settings = data?.currentSettings;

  async function save(values: FormValues) {
    if (!settings) return;
    const fields = toSettingsFields({
      ...values,
      publicArrearsSince: values.publicArrearsSince
        ? values.publicArrearsSince.format('YYYY-MM')
        : '',
    });
    const saved = settings as Record<string, unknown>;
    const changes = Object.entries(fields).filter(
      ([key, value]) => value !== saved[key],
    );
    if (changes.length === 0) {
      message.info('لا تغيير');
      setEditing(false);
      return;
    }
    const ok = await modal.confirm({
      title: 'حفظ الإعدادات',
      width: 560,
      content: (
        <table className="desk-changes">
          <thead>
            <tr>
              <th>الإعداد</th>
              <th>قبل</th>
              <th>بعد</th>
            </tr>
          </thead>
          <tbody>
            {changes.map(([key, value]) => (
              <tr key={key}>
                <td>{LABELS[key as keyof typeof LABELS]}</td>
                <td className="is-before">{show(key, saved[key])}</td>
                <td className="is-after">{show(key, value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ),
      okText: 'حفظ',
      cancelText: 'رجوع',
    });
    if (!ok) return;
    setProblem(null);
    const r = await update({ patch: Object.fromEntries(changes) });
    if (r.error) {
      setProblem(
        describeError(r.error, 'تعذّر حفظ الإعدادات. تحقّق من القيم.', {
          CHECK_VIOLATION: 'قيمة غير مقبولة. تحقّق من الأيام والمبالغ.',
        }),
      );
      return;
    }
    message.success('حُفظت الإعدادات');
    setEditing(false);
  }

  if (!settings) {
    return (
      <section className="desk-page">
        <PageHeader title="الإعدادات" />
        {error ? (
          <Alert type="error" showIcon message="تعذّر تحميل الإعدادات" />
        ) : (
          <Skeleton active paragraph={{ rows: 10 }} />
        )}
      </section>
    );
  }

  const header = (
    <PageHeader
      title="الإعدادات"
      description="الأداء والتذكير، الصور، لائحة المتأخرين العامة. تُطبَّق على الفواتير والرسائل القادمة."
      actions={
        canEdit &&
        (editing ? (
          <>
            <Button
              onClick={() => {
                form.resetFields();
                setProblem(null);
                setEditing(false);
              }}
            >
              إلغاء
            </Button>
            <Button
              type="primary"
              loading={saving}
              onClick={() => form.submit()}
            >
              حفظ الإعدادات
            </Button>
          </>
        ) : (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditing(true)}
          >
            تعديل
          </Button>
        ))
      }
    />
  );

  if (!editing) {
    return (
      <section className="desk-page desk-summary">
        {header}
        {!canEdit && (
          <Alert type="info" showIcon message="للاطلاع فقط: اللجنة تعدّلها." />
        )}
        <SettingsView settings={settings as Record<string, unknown>} />
      </section>
    );
  }

  return (
    <section className="desk-page desk-summary">
      {header}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={!canEdit}
        onFinish={save}
        initialValues={{
          ...settings,
          address: settings.address ?? '',
          contactPhone: settings.contactPhone ?? '',
          publicArrearsMinAmount: Number(settings.publicArrearsMinAmount),
          publicArrearsSince: settings.publicArrearsSince
            ? dayjs(settings.publicArrearsSince)
            : null,
          receiptPhotoMinAmount: Number(settings.receiptPhotoMinAmount),
        }}
      >
        {problem && <Alert type="error" showIcon message={problem} />}

        <div className="desk-summary-grid">
          <section className="desk-card">
            <h2>الجمعية</h2>
            <div className="field-hint">
              تظهر على الفواتير والوصولات والرسائل.
            </div>
            <Form.Item
              name="associationName"
              label="اسم الجمعية"
              rules={[
                { required: true, whitespace: true, message: 'الاسم مطلوب' },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="address" label="العنوان">
              <Input />
            </Form.Item>
            <Form.Item
              name="contactPhone"
              label="هاتف الاستفسار"
              extra={`يُكتب في الرسائل: «للاستفسار اتصلوا بـ…». ${PHONE_HINT}.`}
              rules={[
                {
                  validator: (_, value: string | undefined) => {
                    const result = cleanPhone(value ?? '');
                    return 'error' in result
                      ? Promise.reject(new Error(result.error))
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="tel" className="ltr-num" />
            </Form.Item>
          </section>

          <section className="desk-card">
            <h2>الأداء والتذكير</h2>
            <Form.Item
              name="dueDays"
              label="أجل الأداء (أيام بعد شهر الفاتورة)"
              extra="يُطبّق على الفواتير الجديدة فقط."
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber {...days} />
            </Form.Item>
            <Form.Item
              name="firstReminderAfterDays"
              label="أول تذكير (أيام بعد صدور الفاتورة)"
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber {...days} />
            </Form.Item>
            <Form.Item
              name="reminderEveryDays"
              label="ثم تذكير كل (أيام) حتى الأداء"
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber {...days} />
            </Form.Item>
          </section>

          <section className="desk-card">
            <h2>الصور</h2>
            <Form.Item
              name="readingPhotoAlways"
              label="تصوير العداد مع كل قراءة"
              valuePropName="checked"
              extra="مفعّل: يصوّر القارئ العداد مع كل قراءة. غير مفعّل: لا تُطلب الصورة إلا للقراءات غير المعتادة."
            >
              <Switch />
            </Form.Item>
            <Form.Item
              name="receiptPhotoMinAmount"
              label="المبلغ الذي يستوجب صورة الوصل (درهم)"
              extra="المصاريف من هذا المبلغ فما فوق لا تُسجَّل بدون صورة الوصل."
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber min={0} precision={2} />
            </Form.Item>
          </section>

          <section className="desk-card">
            <h2>لائحة المتأخرين العامة</h2>
            <div className="field-hint">
              الصورة التي تُنشر في مجموعة الدوار عند إغلاق الشهر.
            </div>
            <Form.Item
              name="publicArrearsAfterDays"
              label="يُذكر الاسم بعد تأخر (أيام بعد آخر أجل)"
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber {...days} />
            </Form.Item>
            <Form.Item
              name="publicArrearsRedAfterDays"
              label="يُكتب بالأحمر بعد (أيام على اللائحة)"
              rules={[{ required: true, message: 'مطلوب' }]}
            >
              <InputNumber {...days} />
            </Form.Item>
            <Form.Item
              name="publicArrearsMinAmount"
              label="لا يُذكر إلا إذا بلغ المبلغ المتأخر (درهم)"
              extra="0 = أي مبلغ."
            >
              <InputNumber min={0} precision={2} />
            </Form.Item>
            <Form.Item
              name="publicArrearsSince"
              label="لا تُحتسب إلا الفواتير ابتداءً من شهر"
              extra="فارغ = كل الفواتير."
            >
              <DatePicker picker="month" format="MMMM YYYY" allowClear />
            </Form.Item>
          </section>
        </div>
      </Form>
    </section>
  );
}

const GROUPS: {
  title: string;
  description?: string;
  keys: (keyof typeof LABELS)[];
}[] = [
  {
    title: 'الجمعية',
    description:
      'تظهر على الفواتير والوصولات والرسائل. البطاقة الكاملة في «بطاقة الجمعية».',
    keys: ['associationName', 'address', 'contactPhone'],
  },
  {
    title: 'الأداء والتذكير',
    keys: ['dueDays', 'firstReminderAfterDays', 'reminderEveryDays'],
  },
  {
    title: 'الصور',
    keys: ['readingPhotoAlways', 'receiptPhotoMinAmount'],
  },
  {
    title: 'لائحة المتأخرين العامة',
    keys: [
      'publicArrearsAfterDays',
      'publicArrearsRedAfterDays',
      'publicArrearsMinAmount',
      'publicArrearsSince',
    ],
  },
];

/** The settings read (doc 64): the same groups as the form. */
function SettingsView({ settings }: { settings: Record<string, unknown> }) {
  return (
    <div className="desk-summary-grid">
      {GROUPS.map((g) => (
        <Section key={g.title} title={g.title} description={g.description}>
          <KeyValues
            columns={2}
            items={g.keys.map((key) => ({
              // The card's title already says «لائحة المتأخرين».
              label: LABELS[key].replace('لائحة المتأخرين: ', ''),
              value: show(key, settings[key]),
            }))}
          />
        </Section>
      ))}
    </div>
  );
}
